/**
 * CommunicationsHub - Multi-channel messaging center for event admins
 * Send targeted messages via Email, SMS, Telegram, and WhatsApp
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Send, Mail, Users, Clock, CheckCircle, AlertCircle,
  Loader2, FileText, Filter, Eye, History, Sparkles,
  Smartphone, MessageCircle, Radio, Settings, BarChart3,
  TrendingUp, Activity
} from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';

const CHANNEL_CONFIG = [
  { id: 'email', name: 'Email', icon: Mail, color: 'text-blue-500', bgColor: 'bg-blue-50' },
  { id: 'sms', name: 'SMS', icon: Smartphone, color: 'text-green-500', bgColor: 'bg-green-50' },
  { id: 'telegram', name: 'Telegram', icon: Send, color: 'text-sky-500', bgColor: 'bg-sky-50' },
  { id: 'whatsapp', name: 'WhatsApp', icon: MessageCircle, color: 'text-emerald-500', bgColor: 'bg-emerald-50' }
];

const MESSAGE_TEMPLATES = [
  { id: 'welcome', name: 'Welcome Message', subject: 'Welcome to {{event_name}}!' },
  { id: 'reminder', name: 'Event Reminder', subject: 'Reminder: {{event_name}} starts soon!' },
  { id: 'schedule', name: 'Schedule Update', subject: 'Updated Schedule for {{event_name}}' },
  { id: 'emergency', name: 'Emergency Alert', subject: '🚨 Important Update' },
  { id: 'custom', name: 'Custom Message', subject: '' }
];

const RECIPIENT_FILTERS = [
  { id: 'all', name: 'All Delegates', icon: Users },
  { id: 'confirmed', name: 'Confirmed Only', icon: CheckCircle },
  { id: 'checked_in', name: 'Checked In', icon: CheckCircle },
  { id: 'not_checked_in', name: 'Not Checked In', icon: AlertCircle },
  { id: 'vip', name: 'VIP Tier Only', icon: Sparkles }
];

const CommunicationsHub = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [selectedChannels, setSelectedChannels] = useState(['email']);
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [messageContent, setMessageContent] = useState({
    subject: '',
    message: '',
    html_message: ''
  });
  const [showPreview, setShowPreview] = useState(false);

  // Fetch enabled channels from backend
  const { data: channelStatus } = useQuery({
    queryKey: ['messaging-channels'],
    queryFn: () => api.get('/admin/messaging/channels').then(r => r.data),
  });

  // Fetch broadcast history
  const { data: broadcastHistory = [], isLoading: loadingHistory } = useQuery({
    queryKey: ['broadcast-history', eventId],
    queryFn: () => api.get(`/events/${eventId}/broadcast/history`).then(r => r.data),
  });

  // Fetch recipient count
  const { data: recipientCount } = useQuery({
    queryKey: ['recipient-count', eventId, recipientFilter],
    queryFn: () => api.get(`/events/${eventId}/communications/recipients`, {
      params: { filter: recipientFilter }
    }).then(r => r.data),
  });

  // Fetch communication stats
  const { data: commStats } = useQuery({
    queryKey: ['comm-stats', eventId],
    queryFn: () => api.get(`/events/${eventId}/communications/stats`).then(r => r.data),
  });

  // Send broadcast mutation
  const broadcastMutation = useMutation({
    mutationFn: (data) => api.post(`/events/${eventId}/broadcast`, data),
    onSuccess: (response) => {
      const results = response.data.results || {};
      const successChannels = Object.entries(results)
        .filter(([_, r]) => r.success !== false)
        .map(([ch]) => ch);
      
      if (successChannels.length > 0) {
        toast.success(`Broadcast sent via ${successChannels.join(', ')} to ${response.data.recipients_count} recipients!`);
      }
      
      // Show warnings for failed channels
      Object.entries(results).forEach(([channel, result]) => {
        if (result.success === false) {
          toast.error(`${channel}: ${result.error}`);
        }
      });
      
      queryClient.invalidateQueries(['broadcast-history', eventId]);
      setMessageContent({ subject: '', message: '', html_message: '' });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to send broadcast'))
  });

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = MESSAGE_TEMPLATES.find(t => t.id === templateId);
    if (template && templateId !== 'custom') {
      setMessageContent(prev => ({ ...prev, subject: template.subject }));
    }
  };

  const toggleChannel = (channelId) => {
    setSelectedChannels(prev => {
      if (prev.includes(channelId)) {
        return prev.filter(c => c !== channelId);
      }
      return [...prev, channelId];
    });
  };

  const handleSendBroadcast = () => {
    if (selectedChannels.length === 0) {
      toast.error('Select at least one channel');
      return;
    }
    if (!messageContent.message.trim()) {
      toast.error('Message cannot be empty');
      return;
    }
    if (selectedChannels.includes('email') && !messageContent.subject.trim()) {
      toast.error('Subject is required for email');
      return;
    }

    broadcastMutation.mutate({
      channels: selectedChannels,
      message: messageContent.message,
      subject: messageContent.subject,
      html_message: messageContent.html_message || `<p>${messageContent.message}</p>`,
      recipient_filter: recipientFilter
    });
  };

  const enabledChannels = channelStatus?.enabled_channels || [];

  return (
    <div className="space-y-6" data-testid="communications-hub">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Radio className="h-6 w-6 text-indigo-500" />
            Communications Hub
          </h2>
          <p className="text-slate-500">Send multi-channel broadcasts to your attendees</p>
        </div>
        {enabledChannels.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Active:</span>
            {enabledChannels.map(ch => {
              const config = CHANNEL_CONFIG.find(c => c.id === ch);
              const Icon = config?.icon || Mail;
              return (
                <Badge key={ch} variant="outline" className={`${config?.color || ''}`}>
                  <Icon className="h-3 w-3 mr-1" />
                  {config?.name || ch}
                </Badge>
              );
            })}
          </div>
        )}
      </div>

      <Tabs defaultValue="compose">
        <TabsList>
          <TabsTrigger value="compose" data-testid="compose-tab">
            <Send className="h-4 w-4 mr-2" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">
            <History className="h-4 w-4 mr-2" />
            History ({broadcastHistory.length})
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="stats-tab">
            <BarChart3 className="h-4 w-4 mr-2" />
            Stats
          </TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Compose Panel */}
            <div className="lg:col-span-2 space-y-4">
              {/* Channel Selection */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Select Channels
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {CHANNEL_CONFIG.map(channel => {
                      const Icon = channel.icon;
                      const isEnabled = enabledChannels.includes(channel.id);
                      const isSelected = selectedChannels.includes(channel.id);
                      
                      return (
                        <button
                          key={channel.id}
                          onClick={() => isEnabled && toggleChannel(channel.id)}
                          disabled={!isEnabled}
                          className={`
                            p-4 rounded-lg border-2 transition-all text-left
                            ${isSelected && isEnabled ? `${channel.bgColor} border-current ${channel.color}` : ''}
                            ${isEnabled && !isSelected ? 'hover:bg-slate-50 border-slate-200' : ''}
                            ${!isEnabled ? 'opacity-50 cursor-not-allowed bg-slate-50 border-slate-200' : 'cursor-pointer'}
                          `}
                          data-testid={`channel-${channel.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`p-2 rounded-full ${isSelected && isEnabled ? channel.bgColor : 'bg-slate-100'}`}>
                              <Icon className={`h-5 w-5 ${isSelected && isEnabled ? channel.color : 'text-slate-400'}`} />
                            </div>
                            <div>
                              <p className={`font-medium ${isSelected && isEnabled ? channel.color : 'text-slate-700'}`}>
                                {channel.name}
                              </p>
                              <p className="text-xs text-slate-500">
                                {isEnabled ? (isSelected ? 'Selected' : 'Available') : 'Not configured'}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {enabledChannels.length === 0 && (
                    <Alert className="mt-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        No messaging channels configured. Go to <strong>System Settings</strong> to set up Email, SMS, Telegram, or WhatsApp.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Message Compose */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Compose Message</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Template Selection */}
                  <div>
                    <Label className="mb-2 block">Template</Label>
                    <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a template" />
                      </SelectTrigger>
                      <SelectContent>
                        {MESSAGE_TEMPLATES.map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4" />
                              {t.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subject Line (shown when email is selected) */}
                  {selectedChannels.includes('email') && (
                    <div>
                      <Label className="mb-2 block">Subject Line (Email)</Label>
                      <Input
                        placeholder="Enter email subject..."
                        value={messageContent.subject}
                        onChange={(e) => setMessageContent({ ...messageContent, subject: e.target.value })}
                        data-testid="message-subject"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Use {'{{event_name}}'}, {'{{delegate_name}}'} for personalization
                      </p>
                    </div>
                  )}

                  {/* Message Body */}
                  <div>
                    <Label className="mb-2 block">Message</Label>
                    <Textarea
                      placeholder="Write your message here..."
                      value={messageContent.message}
                      onChange={(e) => setMessageContent({ ...messageContent, message: e.target.value })}
                      rows={8}
                      className="font-mono text-sm"
                      data-testid="message-body"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-slate-500">
                        {messageContent.message.length} characters
                        {selectedChannels.includes('sms') && messageContent.message.length > 160 && (
                          <span className="text-amber-600 ml-2">
                            (SMS: {Math.ceil(messageContent.message.length / 160)} segments)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <Button variant="outline" onClick={() => setShowPreview(true)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                    <Button 
                      onClick={handleSendBroadcast}
                      disabled={broadcastMutation.isPending || selectedChannels.length === 0}
                      data-testid="send-broadcast-btn"
                    >
                      {broadcastMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Broadcast to {recipientCount?.count || 0} Recipients
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recipients Panel */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    Recipients
                  </CardTitle>
                  <CardDescription>Select who receives this broadcast</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {RECIPIENT_FILTERS.map(filter => {
                    const Icon = filter.icon;
                    return (
                      <label
                        key={filter.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          recipientFilter === filter.id 
                            ? 'bg-indigo-50 border-indigo-200' 
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="recipientFilter"
                          value={filter.id}
                          checked={recipientFilter === filter.id}
                          onChange={(e) => setRecipientFilter(e.target.value)}
                          className="sr-only"
                        />
                        <Icon className={`h-5 w-5 ${
                          recipientFilter === filter.id ? 'text-indigo-600' : 'text-slate-400'
                        }`} />
                        <span className={recipientFilter === filter.id ? 'font-medium' : ''}>
                          {filter.name}
                        </span>
                      </label>
                    );
                  })}

                  <div className="pt-4 border-t mt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Total Recipients</span>
                      <Badge variant="secondary" className="text-lg px-3">
                        {recipientCount?.count || 0}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Channel Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-500">Selected Channels</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {selectedChannels.length === 0 ? (
                    <p className="text-sm text-slate-400">No channels selected</p>
                  ) : (
                    selectedChannels.map(channelId => {
                      const config = CHANNEL_CONFIG.find(c => c.id === channelId);
                      const Icon = config?.icon || Mail;
                      return (
                        <div key={channelId} className="flex items-center gap-2 text-sm">
                          <Icon className={`h-4 w-4 ${config?.color}`} />
                          <span>{config?.name}</span>
                          <CheckCircle className="h-3 w-3 text-green-500 ml-auto" />
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              {/* Tips */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-500">Tips</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-slate-600 space-y-2">
                  <p>• Keep SMS under 160 chars for single segment</p>
                  <p>• Telegram supports HTML formatting</p>
                  <p>• Test with a small group first</p>
                  <p>• Personalize with {'{{delegate_name}}'}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Broadcast History</CardTitle>
              <CardDescription>Previously sent communications across all channels</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : broadcastHistory.length > 0 ? (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {broadcastHistory.map((broadcast) => (
                      <div 
                        key={broadcast.id}
                        className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {(broadcast.channels || []).map(ch => {
                                const config = CHANNEL_CONFIG.find(c => c.id === ch);
                                const Icon = config?.icon || Mail;
                                return (
                                  <Badge key={ch} variant="outline" className={config?.color}>
                                    <Icon className="h-3 w-3 mr-1" />
                                    {config?.name || ch}
                                  </Badge>
                                );
                              })}
                            </div>
                            {broadcast.subject && (
                              <p className="font-medium">{broadcast.subject}</p>
                            )}
                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                              {broadcast.message?.substring(0, 150)}
                              {broadcast.message?.length > 150 ? '...' : ''}
                            </p>
                          </div>
                          <Badge variant="secondary">
                            {broadcast.recipients_count} sent
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(broadcast.sent_at).toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Filter className="h-3 w-3" />
                            {broadcast.recipient_filter}
                          </span>
                        </div>
                        {/* Channel results */}
                        {broadcast.results && Object.keys(broadcast.results).length > 0 && (
                          <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                            {Object.entries(broadcast.results).map(([channel, result]) => (
                              <span 
                                key={channel}
                                className={`text-xs px-2 py-1 rounded ${
                                  result.success !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {channel}: {result.success !== false ? (result.sent || 'sent') : result.error}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-12">
                  <Radio className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No broadcasts sent yet</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Compose your first message to get started
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Total Broadcasts */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Broadcasts</p>
                    <p className="text-3xl font-bold">{commStats?.total_broadcasts || 0}</p>
                  </div>
                  <div className="p-3 bg-indigo-100 rounded-full">
                    <Radio className="h-6 w-6 text-indigo-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total Messages Sent */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Messages Sent</p>
                    <p className="text-3xl font-bold">{commStats?.total_messages_sent || 0}</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <Send className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Active Channels */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Active Channels</p>
                    <p className="text-3xl font-bold">{enabledChannels.length}</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Activity className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Last Broadcast */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Last Broadcast</p>
                    <p className="text-lg font-medium">
                      {commStats?.last_broadcast 
                        ? new Date(commStats.last_broadcast).toLocaleDateString()
                        : 'Never'}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-100 rounded-full">
                    <Clock className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Channel Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Delivery by Channel
              </CardTitle>
              <CardDescription>Message delivery statistics across all channels</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {CHANNEL_CONFIG.map(channel => {
                  const Icon = channel.icon;
                  const stats = commStats?.by_channel?.[channel.id] || { sent: 0, failed: 0 };
                  const total = stats.sent + stats.failed;
                  const successRate = total > 0 ? Math.round((stats.sent / total) * 100) : 0;
                  const isEnabled = enabledChannels.includes(channel.id);
                  
                  return (
                    <div 
                      key={channel.id}
                      className={`p-4 rounded-lg border ${isEnabled ? channel.bgColor : 'bg-slate-50'}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-full ${isEnabled ? 'bg-white' : 'bg-slate-100'}`}>
                          <Icon className={`h-5 w-5 ${isEnabled ? channel.color : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${isEnabled ? '' : 'text-slate-400'}`}>{channel.name}</p>
                          <p className="text-xs text-slate-500">
                            {isEnabled ? 'Configured' : 'Not configured'}
                          </p>
                        </div>
                      </div>
                      
                      {isEnabled && (
                        <>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-600">Sent</span>
                            <span className="font-medium text-green-600">{stats.sent}</span>
                          </div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-600">Failed</span>
                            <span className="font-medium text-red-600">{stats.failed}</span>
                          </div>
                          {total > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Success Rate</span>
                                <span className={`font-bold ${successRate >= 90 ? 'text-green-600' : successRate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                                  {successRate}%
                                </span>
                              </div>
                              <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${successRate >= 90 ? 'bg-green-500' : successRate >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                                  style={{ width: `${successRate}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Message Preview</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>×</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Channels */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Channels:</span>
                  {selectedChannels.map(ch => {
                    const config = CHANNEL_CONFIG.find(c => c.id === ch);
                    const Icon = config?.icon || Mail;
                    return (
                      <Badge key={ch} variant="outline" className={config?.color}>
                        <Icon className="h-3 w-3 mr-1" />
                        {config?.name}
                      </Badge>
                    );
                  })}
                </div>

                {/* Preview Box */}
                <div className="border rounded-lg p-6 bg-white">
                  {messageContent.subject && (
                    <div className="border-b pb-4 mb-4">
                      <p className="text-sm text-slate-500">Subject:</p>
                      <p className="font-medium">{messageContent.subject}</p>
                    </div>
                  )}
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap font-sans text-slate-700">
                      {messageContent.message || '(No content)'}
                    </pre>
                  </div>
                </div>

                {/* Recipients */}
                <div className="text-sm text-slate-500">
                  Sending to: <strong>{recipientCount?.count || 0}</strong> {recipientFilter} recipients
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={() => setShowPreview(false)}>Close Preview</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CommunicationsHub;
